import { useEffect, useState } from "react";
import { PauseCircle, Play, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { PORTAL_META, saveMaintenanceConfig, useMaintenance } from "../../lib/maintenance";
import MaintenanceScreen from "../../components/MaintenanceScreen";

export default function SiteStatus() {
  const { user, getAuthHeaders } = useAuth();
  const { cfg, loaded, refresh } = useMaintenance();
  const [headline, setHeadline] = useState(cfg.headline);
  const [message, setMessage] = useState(cfg.message);
  const [portals, setPortals] = useState(cfg.portals);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!loaded) return;
    setHeadline(cfg.headline);
    setMessage(cfg.message);
    setPortals(cfg.portals);
  }, [loaded, cfg.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (next, revertPortals) => {
    setSaving(true);
    setMsg("");
    try {
      const data = await saveMaintenanceConfig(
        {
          headline: next.headline,
          message: next.message,
          portals: next.portals,
        },
        getAuthHeaders(),
      );
      setHeadline(data.headline);
      setMessage(data.message);
      setPortals(data.portals);
      await refresh();
      setMsg("Site status saved. Visitors will see the change within a few seconds.");
    } catch (err) {
      if (revertPortals) setPortals(revertPortals);
      const detail = err.response?.data?.detail;
      setMsg(typeof detail === "string" ? detail : "Could not save site status. Check that you are logged in as super admin.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => {
    const prev = portals;
    const next = { ...portals, [id]: !portals[id] };
    setPortals(next);
    persist({ headline, message, portals: next }, prev);
  };

  const pauseAll = () => {
    const prev = portals;
    const next = Object.fromEntries(PORTAL_META.map((p) => [p.id, true]));
    setPortals(next);
    persist({ headline, message, portals: next }, prev);
  };

  const resumeAll = () => {
    const prev = portals;
    const next = Object.fromEntries(PORTAL_META.map((p) => [p.id, false]));
    setPortals(next);
    persist({ headline, message, portals: next }, prev);
  };

  const anyPaused = PORTAL_META.some((p) => portals[p.id]);

  if (user?.role && user.role !== "super_admin") {
    return <p className="text-sm text-[#6b7280]">Only a super admin can change site status.</p>;
  }

  return (
    <div className="space-y-6" data-testid="site-status">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Site status</h1>
          <p className="text-sm text-[#6b7280] mt-1 max-w-2xl">
            Pause any portal with a graceful page. Super admin stays available so you can turn things back on.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={pauseAll}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#311b52] text-white px-4 py-2 text-sm font-semibold"
          >
            <PauseCircle className="w-4 h-4" /> Pause all
          </button>
          <button
            type="button"
            onClick={resumeAll}
            disabled={saving || !anyPaused}
            className="inline-flex items-center gap-2 rounded-full border border-[#673c96] text-[#673c96] px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            <Play className="w-4 h-4" /> Resume all
          </button>
        </div>
      </div>

      {msg && <p className="text-sm text-[#673c96]">{msg}</p>}

      <div className="grid lg:grid-cols-2 gap-4">
        {PORTAL_META.map((portal) => {
          const on = Boolean(portals[portal.id]);
          return (
            <button
              key={portal.id}
              type="button"
              onClick={() => toggle(portal.id)}
              disabled={saving}
              className={`text-left rounded-2xl border p-5 transition ${
                on ? "border-[#f37721] bg-[#fff7f1]" : "border-[#673c96]/15 bg-white hover:border-[#673c96]/40"
              }`}
              data-testid={`pause-${portal.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#311b52]">{portal.label}</p>
                  <p className="text-xs text-[#6b7280] mt-1">{portal.hint}</p>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold ${
                  on ? "bg-[#f37721] text-white" : "bg-emerald-100 text-emerald-800"
                }`}>
                  {on ? "Paused" : "Live"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-[#673c96]/10 p-5 space-y-3">
        <div className="flex items-center gap-2 text-[#311b52]">
          <ShieldCheck className="w-4 h-4 text-[#673c96]" />
          <h2 className="font-semibold">Visitor message</h2>
        </div>
        <input
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          maxLength={120}
          className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3 text-sm"
          placeholder="We're working on this page"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={400}
          rows={3}
          className="w-full rounded-xl border border-[#e5e7eb] px-4 py-3 text-sm"
          placeholder="to bring good changes for you"
        />
        <button
          type="button"
          disabled={saving}
          onClick={() => persist({ headline, message, portals })}
          className="rounded-full bg-[#f37721] text-white px-5 py-2.5 text-sm font-semibold"
        >
          Save message
        </button>
      </div>

      <div>
        <p className="text-xs font-semibold text-[#6b7280] mb-2 uppercase tracking-wide">Preview</p>
        <div className="rounded-2xl overflow-hidden border border-[#673c96]/10">
          <MaintenanceScreen compact headline={headline} message={message} />
        </div>
      </div>
    </div>
  );
}
